import { QueryClient } from '@tanstack/react-query';
import { createClient, createOutbox, OfflineError, type FormDataCtor } from '@zibanu/client';
import regularFont from '@zibanu/ui/fonts/Archivo-Regular.woff2?url';
import boldFont from '@zibanu/ui/fonts/Archivo-Bold.woff2?url';
import '@zibanu/ui/styles.css';
import './app/app.css';
import { ActionError } from './app/data';
import { CACHE_MAX_AGE_MS, createDevicePersister } from './app/cache';
import { createInstallStore } from './app/install';
import { deviceLabel, idbAsyncStorage, idbStorage, openWhatsApp } from './app/platform';
import { watchPredrawn } from './app/predrawn';
import { createAppRouter } from './app/router';
import { startApp } from './app/start';
import { applyTheme, savedTheme } from './app/theme';
import { AppUpdates } from './app/updates';

// First, so an install offer that comes early isn't missed (spec 3A §6.5).
const install = createInstallStore(window);
// What's typed into a pre-drawn login.html is followed until the app replaces it (issue #8).
watchPredrawn();

// The two weights the first screen uses load first (spec 3A §5.3); the rest load when needed.
for (const href of [regularFont, boldFont]) {
  // login.html already preloads them (issue #8).
  if (document.querySelector(`link[rel="preload"][href="${href}"]`)) continue;
  const link = document.createElement('link');
  Object.assign(link, { rel: 'preload', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous', href });
  document.head.appendChild(link);
}

/** No request waits forever: a hung mobile connection would freeze logout, which waits on the outbox lock. A timeout rejects, the client calls it offline, and a capture stays queued. */
const REQUEST_TIMEOUT_MS = 60_000;
/** A voice note can be minutes of audio on a slow uplink. */
const UPLOAD_TIMEOUT_MS = 180_000;

/** AbortSignal.timeout is missing before Chrome 103 / Safari 16: fall back to a timer. */
function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

const client = createClient({
  baseUrl: import.meta.env.VITE_API_ORIGIN,
  fetch: (url, init) => fetch(url, { ...(init as RequestInit), signal: timeoutSignal(init.body instanceof FormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS) }),
  FormData: window.FormData as unknown as FormDataCtor,
  storage: idbStorage,
});
const outbox = createOutbox({ client, storage: idbStorage });
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, gcTime: CACHE_MAX_AGE_MS, retry: (count, err) => !(err instanceof OfflineError) && !(err instanceof ActionError) && count < 2 },
  },
});
const persister = createDevicePersister(idbAsyncStorage);
const router = createAppRouter();

applyTheme(savedTheme());

void startApp(
  document.getElementById('root')!,
  {
    client,
    outbox,
    queryClient,
    persister,
    platform: { deviceLabel: () => deviceLabel(), openWhatsApp: () => openWhatsApp(), now: () => new Date(), install },
  },
  router,
  <AppUpdates />,
);
